import { action } from "typesafe-actions";
import {
  ICampaignRuleTemplate,
  IMarketCampaignBasicInfo,
  IMarketCampaignAudience,
  IMarketCampaignChannel,
  IMarketCampaignCronScheduler,
  initialMarketCampaignState,
  IMarketCampaignDraftStep,
  IAudienceListColumn,
} from "./marketCampaign.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import {
  closeLoaderWithMessage,
  openLoaderWithMessage,
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { getFileType } from "../../helpers/fileTypeDetector";
import { uploadFile } from "../../components/FileUpload/utils";

export const FETCH_MARKET_CAMPAIGN_LIST_PROGRESS =
  "FETCH_MARKET_CAMPAIGN_LIST_PROGRESS";
export const FETCH_MARKET_CAMPAIGN_LIST_SUCCESS =
  "FETCH_MARKET_CAMPAIGN_LIST_SUCCESS";
export const FETCH_MARKET_CAMPAIGN_LIST_FAILED =
  "FETCH_MARKET_CAMPAIGN_LIST_FAILED";

export const fetchMarketCampaignListProgress = () =>
  action(FETCH_MARKET_CAMPAIGN_LIST_PROGRESS);
export const fetchMarketCampaignListSuccess = (
  list: IMarketCampaignBasicInfo[],
  totalRecords: number,
  currentRecords: number,
  meta: { use_count: number; created_count: number }
) =>
  action(FETCH_MARKET_CAMPAIGN_LIST_SUCCESS, {
    list,
    totalRecords,
    currentRecords,
    meta,
  });
export const fetchMarketCampaignListFailed = () =>
  action(FETCH_MARKET_CAMPAIGN_LIST_FAILED);

interface IFetchMarketCampaignListAsyncParams {
  pageNumber: number;
  rowsInPerPage: number;
  limit: number;
  status: string;
  date: IDataTableV2DateState["dates"];
  searchValue: IDatatableV2AdvancedSearchFilter;
  company_outlet_uuid?: string;
  searchByName?: string;
}

export const fetchMarketCampaignListAsync =
  (
    params: IFetchMarketCampaignListAsyncParams
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchMarketCampaignListProgress());
      dispatch(saveLoaderProgress());
      let url = `/campaign/get-campaigns?pageNo=${params.pageNumber}&pageLimit=${params.limit}&itemPerPage=${params.rowsInPerPage}`;

      if (params.status !== "-1") url += `&status=${params.status}`;
      if (params.searchValue.length > 0 || params.searchByName) {
        const advanceFilter = [...params.searchValue];

        if (params.searchByName) {
          advanceFilter.push({
            column: ["campaign_name"],
            value: params.searchByName,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        url += `&advanceFilter=${encodeURIComponent(
          JSON.stringify(advanceFilter)
        )}`;
      }

      if (params.company_outlet_uuid)
        url += `&company_outlet_uuid=${params.company_outlet_uuid}`;
      if (params.date)
        url += `&from_date=${params.date.fromDate}&to_date=${params.date.toDate}`;

      const res = await api.get(url);
      dispatch(
        fetchMarketCampaignListSuccess(
          res.data.data,
          res.data.totalRecords,
          res.data.currentRecords,
          res.data.meta
        )
      );
    } catch (err: any) {
      dispatch(fetchMarketCampaignListFailed());

      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to fetch campaigns",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_MARKET_CAMPAIGN_AUDIENCE_LIST_PROGRESS =
  "FETCH_MARKET_CAMPAIGN_AUDIENCE_LIST_PROGRESS";
export const FETCH_MARKET_CAMPAIGN_AUDIENCE_LIST_SUCCESS =
  "FETCH_MARKET_CAMPAIGN_AUDIENCE_LIST_SUCCESS";
export const FETCH_MARKET_CAMPAIGN_AUDIENCE_LIST_FAILED =
  "FETCH_MARKET_CAMPAIGN_AUDIENCE_LIST_FAILED";

export const fetchMarketCampaignAudienceListProgress = () =>
  action(FETCH_MARKET_CAMPAIGN_AUDIENCE_LIST_PROGRESS);
export const fetchMarketCampaignAudienceListSuccess = (
  list: IMarketCampaignAudience[]
) => action(FETCH_MARKET_CAMPAIGN_AUDIENCE_LIST_SUCCESS, { list });
export const fetchMarketCampaignAudienceListFailed = () =>
  action(FETCH_MARKET_CAMPAIGN_AUDIENCE_LIST_FAILED);

export const fetchMarketCampaignAudienceListAsync =
  (marketCampaignUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchMarketCampaignAudienceListProgress());
      const res = await api.get(
        `/campaign/get-market-campaign-audiences?market_campaign_uuid=${marketCampaignUuid}`
      );
      dispatch(fetchMarketCampaignAudienceListSuccess(res.data.data));
    } catch (err: any) {
      dispatch(fetchMarketCampaignAudienceListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch campaign audiences",
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_MARKET_CAMPAIGN_CHANNEL_LIST_PROGRESS =
  "FETCH_MARKET_CAMPAIGN_CHANNEL_LIST_PROGRESS";
export const FETCH_MARKET_CAMPAIGN_CHANNEL_LIST_SUCCESS =
  "FETCH_MARKET_CAMPAIGN_CHANNEL_LIST_SUCCESS";
export const FETCH_MARKET_CAMPAIGN_CHANNEL_LIST_FAILED =
  "FETCH_MARKET_CAMPAIGN_CHANNEL_LIST_FAILED";

export const fetchMarketCampaignChannelListProgress = () =>
  action(FETCH_MARKET_CAMPAIGN_CHANNEL_LIST_PROGRESS);
export const fetchMarketCampaignChannelListSuccess = (
  data: IMarketCampaignChannel
) => action(FETCH_MARKET_CAMPAIGN_CHANNEL_LIST_SUCCESS, { data });
export const fetchMarketCampaignChannelListFailed = () =>
  action(FETCH_MARKET_CAMPAIGN_CHANNEL_LIST_FAILED);

export const fetchMarketCampaignChannelListAsync =
  (marketCampaignUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchMarketCampaignChannelListProgress());
      const res = await api.get(
        `/campaign/get-market-campaign-channels?market_campaign_uuid=${marketCampaignUuid}`
      );
      dispatch(fetchMarketCampaignChannelListSuccess(res.data.data[0]));
    } catch (err: any) {
      dispatch(fetchMarketCampaignChannelListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch campaign channels",
          displayAs: "snackbar",
        })
      );
    }
  };

interface ISaveQuickSendCampaign {
  market_campaign_uuid: string;
  quick_send_list: any[];
  new_quick_send_list: any[];
  check_all: boolean;
}
export const saveQuickSendCampaignAsync =
  (
    data: ISaveQuickSendCampaign,
    onSuccess?: (data: ISaveQuickSendCampaign) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      await api.post("/campaign/save-quick-send-campaign", data);

      onSuccess?.(data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to save quick send campaign",
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertMarketCampaignCronSchedulerAsync =
  (
    data: IMarketCampaignCronScheduler,
    onSuccess?: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const res = await api.post(
        "/campaign/upsert-market-campaign-cron-scheduler",
        data
      );

      onSuccess?.();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to update cron scheduler",
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertMarketCampaignBasicInfoAsync =
  (
    data: IMarketCampaignBasicInfo,
    onSuccess?: (data: IMarketCampaignBasicInfo) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post("/campaign/upsert-campaign", data);
      onSuccess?.(res.data.data);
      dispatch(
        showMessage({
          type: "success",
          message: "Campaign basic info saved successfully",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to save campaign basic info",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertMarketCampaignAudienceAsync =
  (
    data: IMarketCampaignAudience,
    onSuccess?: (data: IMarketCampaignAudience) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(
        "/campaign/upsert-market-campaign-audience",
        data
      );
      onSuccess?.(res.data.data);
      dispatch(
        showMessage({
          type: "success",
          message: "Campaign audience saved successfully",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to save campaign audience",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const runQuickCampaign =
  (
    marketId: string,
    onCallBack: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/campaign/execute-quick-campaign/" + marketId);
      onCallBack(true);

      dispatch(
        showMessage({
          type: "success",
          message: "Campaign has been created successfully.",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      onCallBack(false);
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to save campaign audience",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertMarketCampaignChannelAsync =
  (
    data: IMarketCampaignChannel,
    file?: any | null,
    onSuccess?: (data: IMarketCampaignChannel, path?: string) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());

      let path = "";
      if (file) {
        path = await uploadFile({
          file: file,
          module_name: "WHATSAPP_MARKET_CAMPAIGN",
          defaultReturn: data.header_media[0].value || "",
          asPayload: {},
          onWarning: (message) => {
            dispatch(
              showMessage({
                type: "warning",
                message: message,
                displayAs: "snackbar",
              })
            );
          },
        });
      }

      let res;
      if (file) {
        res = await api.post("/campaign/upsert-market-campaign-channel", {
          ...data,
          attachment:
            "https://enr-biolerplate.s3.ap-south-1.amazonaws.com/" + path,
          header_media: [
            {
              type: data.header_media[0].type,
              value:
                "https://enr-biolerplate.s3.ap-south-1.amazonaws.com/" + path,
            },
          ],
        });
      } else {
        res = await api.post("/campaign/upsert-market-campaign-channel", {
          ...data,
        });
      }

      onSuccess?.(res.data.data, path);
      dispatch(
        showMessage({
          type: "success",
          message: "Campaign channel saved successfully",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to save campaign channel",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_CAMPAIGN_RULE_TEMPLATES_PROGRESS =
  "FETCH_CAMPAIGN_RULE_TEMPLATES_PROGRESS";
export const FETCH_CAMPAIGN_RULE_TEMPLATES_SUCCESS =
  "FETCH_CAMPAIGN_RULE_TEMPLATES_SUCCESS";
export const FETCH_CAMPAIGN_RULE_TEMPLATES_FAILED =
  "FETCH_CAMPAIGN_RULE_TEMPLATES_FAILED";

export const fetchCampaignRuleTemplatesProgress = () =>
  action(FETCH_CAMPAIGN_RULE_TEMPLATES_PROGRESS);
export const fetchCampaignRuleTemplatesSuccess = (
  list: ICampaignRuleTemplate[]
) => action(FETCH_CAMPAIGN_RULE_TEMPLATES_SUCCESS, { list });
export const fetchCampaignRuleTemplatesFailed = () =>
  action(FETCH_CAMPAIGN_RULE_TEMPLATES_FAILED);

export const fetchCampaignRuleTemplatesAsync =
  (
    company_outlet_uuid: string,
    marketCampaignUuid?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchCampaignRuleTemplatesProgress());

      const res = await api.get(
        `/campaign/get-campaign-rule-templates?company_outlet_uuid=${company_outlet_uuid}&market_campaign_uuid=${marketCampaignUuid}`
      );
      const data: ICampaignRuleTemplate[] = res.data.data;

      dispatch(fetchCampaignRuleTemplatesSuccess(data));
    } catch (err: any) {
      dispatch(fetchCampaignRuleTemplatesFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_MARKETING_CAMPAIGN_LOGS_PROGRESS =
  "FETCH_MARKETING_CAMPAIGN_LOGS_PROGRESS";
export const FETCH_MARKETING_CAMPAIGN_LOGS_SUCCESS =
  "FETCH_MARKETING_CAMPAIGN_LOGS_SUCCESS";
export const FETCH_MARKETING_CAMPAIGN_LOGS_FAILED =
  "FETCH_MARKETING_CAMPAIGN_LOGS_FAILED";

export const fetchMarketingCampaignLogsProgress = () =>
  action(FETCH_MARKETING_CAMPAIGN_LOGS_PROGRESS);
export const fetchMarketingCampaignLogsSuccess = (list: any[]) =>
  action(FETCH_MARKETING_CAMPAIGN_LOGS_SUCCESS, { list });
export const fetchMarketingCampaignLogsFailed = () =>
  action(FETCH_MARKETING_CAMPAIGN_LOGS_FAILED);

export const fetchMarketingCampaignLogsAsync =
  (
    campaignId: string,
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date?: IDataTableV2DateState["dates"]
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchMarketingCampaignLogsProgress());
      dispatch(openLoaderWithMessage("Hold on! We're fetching your data."));

      let url = `/campaign/get-campaign-logs?market_campaign_uuid=${campaignId}&pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}`;
      if (status !== "-1") {
        url += `&status=${status}`;
      }
      // if (date) {
      //   url += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
      // }
      if (rowsInPerPage) {
        url += `&pageLimit=${limit}`;
      }
      const res = await api.get(url);
      const data = res.data.data;

      dispatch(fetchMarketingCampaignLogsSuccess(data));
    } catch (err: any) {
      dispatch(fetchMarketingCampaignLogsFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(closeLoaderWithMessage());
    }
  };

// Single Market Campaign Actions
export const FETCH_MARKET_CAMPAIGN_PROGRESS = "FETCH_MARKET_CAMPAIGN_PROGRESS";
export const FETCH_MARKET_CAMPAIGN_SUCCESS = "FETCH_MARKET_CAMPAIGN_SUCCESS";
export const FETCH_MARKET_CAMPAIGN_FAILED = "FETCH_MARKET_CAMPAIGN_FAILED";

export const fetchMarketCampaignProgress = () =>
  action(FETCH_MARKET_CAMPAIGN_PROGRESS);
export const fetchMarketCampaignSuccess = (data: IMarketCampaignBasicInfo) =>
  action(FETCH_MARKET_CAMPAIGN_SUCCESS, { data });
export const fetchMarketCampaignFailed = (errorMessage: string) =>
  action(FETCH_MARKET_CAMPAIGN_FAILED, { errorMessage });

export const fetchMarketCampaignAsync =
  (
    marketCampaignUuid: string,
    onCallBack: (
      isSuccess: boolean,
      dataFound: IMarketCampaignBasicInfo
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchMarketCampaignProgress());
      const res = await api.get(
        `/campaign/get-campaigns?market_campaign_uuid=${marketCampaignUuid}`
      );

      if (res.data.data.length > 0) {
        dispatch(fetchMarketCampaignSuccess(res.data.data[0]));
        onCallBack(true, res.data.data);
      } else {
        dispatch(fetchMarketCampaignFailed("Campaign not found"));
      }
    } catch (err: any) {
      onCallBack(false, err);
      dispatch(
        fetchMarketCampaignFailed(
          err.response?.data?.message || "Failed to fetch campaign"
        )
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to fetch campaign",
          displayAs: "snackbar",
        })
      );
    }
  };

// Step 1 - Basic Info Actions
export const fetchMarketCampaignBasicInfoAsync =
  (marketCampaignUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchMarketCampaignProgress());
      const res = await api.get(
        `/campaign/get-campaigns?market_campaign_uuid=${marketCampaignUuid}`
      );

      if (res.data.data.length > 0) {
        dispatch(fetchMarketCampaignSuccess(res.data.data[0]));
      } else {
        dispatch(fetchMarketCampaignFailed("Campaign not found"));
      }
    } catch (err: any) {
      dispatch(
        fetchMarketCampaignFailed(
          err.response?.data?.message || "Failed to fetch campaign"
        )
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to fetch campaign",
          displayAs: "snackbar",
        })
      );
    }
  };

// Step 2 - Audience Actions
export const FETCH_MARKET_CAMPAIGN_AUDIENCE_PROGRESS =
  "FETCH_MARKET_CAMPAIGN_AUDIENCE_PROGRESS";
export const FETCH_MARKET_CAMPAIGN_AUDIENCE_SUCCESS =
  "FETCH_MARKET_CAMPAIGN_AUDIENCE_SUCCESS";
export const FETCH_MARKET_CAMPAIGN_AUDIENCE_FAILED =
  "FETCH_MARKET_CAMPAIGN_AUDIENCE_FAILED";

export const fetchMarketCampaignAudienceProgress = () =>
  action(FETCH_MARKET_CAMPAIGN_AUDIENCE_PROGRESS);
export const fetchMarketCampaignAudienceSuccess = (
  data: IMarketCampaignAudience
) => action(FETCH_MARKET_CAMPAIGN_AUDIENCE_SUCCESS, { data });
export const fetchMarketCampaignAudienceFailed = () =>
  action(FETCH_MARKET_CAMPAIGN_AUDIENCE_FAILED);

export const fetchMarketCampaignAudienceAsync =
  (
    marketCampaignUuid: string,
    onCallBack: (
      isSuccess: boolean,
      dataFound?: IMarketCampaignAudience
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchMarketCampaignAudienceProgress());
      const res = await api.get(
        `/campaign/get-market-campaign-audiences?market_campaign_uuid=${marketCampaignUuid}`
      );
      const data = res.data.data;
      if (data.length > 0) {
        onCallBack(true, data[0]);
        dispatch(fetchMarketCampaignAudienceSuccess(res.data.data[0]));
      } else {
        onCallBack(true);
        dispatch(
          fetchMarketCampaignAudienceSuccess(
            initialMarketCampaignState.marketCampaignAudience.data
          )
        );
      }
    } catch (err: any) {
      onCallBack(false);
      dispatch(fetchMarketCampaignAudienceFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch campaign audiences",
          displayAs: "snackbar",
        })
      );
    }
  };

// ... similar patterns for Step 3 (Channel) and Step 4 (Schedule)

export const FETCH_QUICK_SEND_CAMPAIGN_LIST_PROGRESS =
  "FETCH_QUICK_SEND_CAMPAIGN_LIST_PROGRESS";
export const FETCH_QUICK_SEND_CAMPAIGN_LIST_SUCCESS =
  "FETCH_QUICK_SEND_CAMPAIGN_LIST_SUCCESS";
export const FETCH_QUICK_SEND_CAMPAIGN_LIST_FAILED =
  "FETCH_QUICK_SEND_CAMPAIGN_LIST_FAILED";

export const fetchQuickSendCampaignListProgress = () =>
  action(FETCH_QUICK_SEND_CAMPAIGN_LIST_PROGRESS);
export const fetchQuickSendCampaignListSuccess = (
  list: any[],
  headers: any[],
  totalRecords: number
) =>
  action(FETCH_QUICK_SEND_CAMPAIGN_LIST_SUCCESS, {
    list,
    headers,
    totalRecords,
  });
export const fetchQuickSendCampaignListFailed = () =>
  action(FETCH_QUICK_SEND_CAMPAIGN_LIST_FAILED);

export const fetchQuickSendCampaignListAsync =
  (
    market_campaign_uuid: string,
    company_outlet_uuid: string,
    pageNo: number,
    phone?: string,
    name?: string,
    itemPerPage?: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchQuickSendCampaignListProgress());

      let url = `/campaign/get-quick-send-campaign-list?market_campaign_uuid=${market_campaign_uuid}&itemPerPage=${itemPerPage}`;

      if (company_outlet_uuid)
        url += `&company_outlet_uuid=${company_outlet_uuid}`;

      if (pageNo) url += `&pageNo=${pageNo}`;

      if (phone) url += `&phone=${phone}`;
      if (name) url += `&name=${name}`;

      const res = await api.get(url);

      let headers = res.data.data.headers as string[];

      if (!headers.includes("check")) {
        headers = ["check"].concat(headers);
      }

      dispatch(
        fetchQuickSendCampaignListSuccess(
          res.data.data.quick_send_list,
          headers,
          res.data.totalRecords
        )
      );
    } catch (err: any) {
      dispatch(fetchQuickSendCampaignListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message ||
            "Failed to fetch quick send campaigns",
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_MARKET_CAMPAIGN_CRON_SCHEDULERS_PROGRESS =
  "FETCH_MARKET_CAMPAIGN_CRON_SCHEDULERS_PROGRESS";
export const FETCH_MARKET_CAMPAIGN_CRON_SCHEDULERS_SUCCESS =
  "FETCH_MARKET_CAMPAIGN_CRON_SCHEDULERS_SUCCESS";
export const FETCH_MARKET_CAMPAIGN_CRON_SCHEDULERS_FAILED =
  "FETCH_MARKET_CAMPAIGN_CRON_SCHEDULERS_FAILED";

export const fetchMarketCampaignCronSchedulersProgress = () =>
  action(FETCH_MARKET_CAMPAIGN_CRON_SCHEDULERS_PROGRESS);
export const fetchMarketCampaignCronSchedulersSuccess = (
  data: IMarketCampaignCronScheduler
) => action(FETCH_MARKET_CAMPAIGN_CRON_SCHEDULERS_SUCCESS, { data });
export const fetchMarketCampaignCronSchedulersFailed = () =>
  action(FETCH_MARKET_CAMPAIGN_CRON_SCHEDULERS_FAILED);

export const fetchMarketCampaignCronSchedulersAsync =
  (marketCampaignUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchMarketCampaignCronSchedulersProgress());
      const res = await api.get(
        `/campaign/get-market-campaign-cron-schedulers?market_campaign_uuid=${marketCampaignUuid}`
      );
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchMarketCampaignCronSchedulersSuccess(res.data.data[0]));
      } else {
        dispatch(
          fetchMarketCampaignCronSchedulersSuccess(
            initialMarketCampaignState.marketCampaignCronScheduler.data
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchMarketCampaignCronSchedulersFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message ||
            "Failed to fetch campaign schedulers",
          displayAs: "snackbar",
        })
      );
    }
  };

// Action types for fetching result of campaign SQL query
export const FETCH_CAMPAIGN_SQL_QUERY_RESULT_PROGRESS =
  "FETCH_CAMPAIGN_SQL_QUERY_RESULT_PROGRESS";
export const FETCH_CAMPAIGN_SQL_QUERY_RESULT_SUCCESS =
  "FETCH_CAMPAIGN_SQL_QUERY_RESULT_SUCCESS";
export const FETCH_CAMPAIGN_SQL_QUERY_RESULT_FAILED =
  "FETCH_CAMPAIGN_SQL_QUERY_RESULT_FAILED";

// Action creators
export const fetchCampaignSqlQueryResultProgress = () =>
  action(FETCH_CAMPAIGN_SQL_QUERY_RESULT_PROGRESS);
export const fetchCampaignSqlQueryResultSuccess = (list: any[]) =>
  action(FETCH_CAMPAIGN_SQL_QUERY_RESULT_SUCCESS, { list });
export const fetchCampaignSqlQueryResultFailed = (errorMessage: string) =>
  action(FETCH_CAMPAIGN_SQL_QUERY_RESULT_FAILED, { errorMessage });

// Thunk action for fetching result of campaign SQL query (GET)
export const fetchCampaignSqlQueryResultAsync =
  (
    market_campaign_uuid: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchCampaignSqlQueryResultProgress());
      const res = await api.get(
        `/campaign/get-result-of-campaign-sql-query?market_campaign_uuid=${market_campaign_uuid}`
      );
      dispatch(fetchCampaignSqlQueryResultSuccess(res.data.data));
    } catch (err: any) {
      const message =
        err.response?.data?.message || "Failed to fetch SQL query result";
      dispatch(fetchCampaignSqlQueryResultFailed(message));
      dispatch(
        showMessage({
          type: "error",
          message,
          displayAs: "snackbar",
        })
      );
    }
  };

// FETCH ALL MARKET CAMPAIGN AUDIENCE LIST
export const FETCH_ALL_MARKET_CAMPAIGN_AUDIENCE_LIST_PROGRESS =
  "FETCH_ALL_MARKET_CAMPAIGN_AUDIENCE_LIST_PROGRESS";
export const FETCH_ALL_MARKET_CAMPAIGN_AUDIENCE_LIST_SUCCESS =
  "FETCH_ALL_MARKET_CAMPAIGN_AUDIENCE_LIST_SUCCESS";
export const FETCH_ALL_MARKET_CAMPAIGN_AUDIENCE_LIST_FAILED =
  "FETCH_ALL_MARKET_CAMPAIGN_AUDIENCE_LIST_FAILED";

export const fetchAllMarketCampaignAudienceListProgress = () =>
  action(FETCH_ALL_MARKET_CAMPAIGN_AUDIENCE_LIST_PROGRESS);
export const fetchAllMarketCampaignAudienceListSuccess = (
  list: IMarketCampaignAudience[],
  totalRecords?: number
) =>
  action(FETCH_ALL_MARKET_CAMPAIGN_AUDIENCE_LIST_SUCCESS, {
    list,
    totalRecords: totalRecords || 0,
  });
export const fetchAllMarketCampaignAudienceListFailed = () =>
  action(FETCH_ALL_MARKET_CAMPAIGN_AUDIENCE_LIST_FAILED);

export const fetchAllMarketCampaignAudienceListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    company_outlet_uuid: string,
    searchByName?: string,
    market_campaign_audience_list_uuid?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchAllMarketCampaignAudienceListProgress());
      dispatch(saveLoaderProgress());

      let url = `/campaign/get-market-campaign-audience-list?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&company_outlet_uuid=${company_outlet_uuid}`;

      if (status !== "-1") url += `&status=${status}`;
      if (searchValue.length > 0)
        url += `&advanceFilter=${JSON.stringify(searchValue)}`;
      if (date && date.fromDate && date.toDate)
        url += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (market_campaign_audience_list_uuid) {
        url += `&market_campaign_audience_list_uuid=${market_campaign_audience_list_uuid}`;
      }
      if (searchByName) {
        url += `&columns=audience_name&value=${searchByName}`;
      }

      const res = await api.get(url);

      const totalRecords =
        res.data.totalRecords ?? res.data.total_records ?? res.data.total ?? 0;

      dispatch(
        fetchAllMarketCampaignAudienceListSuccess(res.data.data, totalRecords)
      );
    } catch (err: any) {
      dispatch(fetchAllMarketCampaignAudienceListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message ||
            "Failed to fetch all campaign audiences",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// SAVE MARKET CAMPAIGN AUDIENCE LIST
export const SAVE_MARKET_CAMPAIGN_AUDIENCE_LIST_PROGRESS =
  "SAVE_MARKET_CAMPAIGN_AUDIENCE_LIST_PROGRESS";
export const SAVE_MARKET_CAMPAIGN_AUDIENCE_LIST_SUCCESS =
  "SAVE_MARKET_CAMPAIGN_AUDIENCE_LIST_SUCCESS";
export const SAVE_MARKET_CAMPAIGN_AUDIENCE_LIST_FAILED =
  "SAVE_MARKET_CAMPAIGN_AUDIENCE_LIST_FAILED";

export const saveMarketCampaignAudienceListProgress = () =>
  action(SAVE_MARKET_CAMPAIGN_AUDIENCE_LIST_PROGRESS);
export const saveMarketCampaignAudienceListSuccess = (data: any) =>
  action(SAVE_MARKET_CAMPAIGN_AUDIENCE_LIST_SUCCESS, { data });
export const saveMarketCampaignAudienceListFailed = () =>
  action(SAVE_MARKET_CAMPAIGN_AUDIENCE_LIST_FAILED);

export const saveMarketCampaignAudienceListAsync =
  (
    payload: any,
    onSuccess?: (data: IMarketCampaignAudience) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveMarketCampaignAudienceListProgress());

      // Auto-detect source type from the first file
      const sourceType =
        payload.source_link.length > 0
          ? getFileType(payload.source_link[0])
          : "CSV"; // Default if no files

      const requestPayload = {
        ...payload,
        audience_source: sourceType,
      };

      const res = await api.post(
        "/campaign/save-market-campaign-audience-list",
        requestPayload
      );
      dispatch(saveMarketCampaignAudienceListSuccess(res.data.data));
      dispatch(
        showMessage({
          type: "success",
          message: "Audience list saved successfully",
          displayAs: "snackbar",
        })
      );
      return res.data.data;
    } catch (err: any) {
      dispatch(saveMarketCampaignAudienceListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to save audience list",
          displayAs: "snackbar",
        })
      );
      throw err;
    }
  };

export const saveMarketCampaignAudienceListAsyncV2 =
  (
    payload: any,
    onSuccess: (isSuccess: boolean, data?: IMarketCampaignAudience) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const { localFile, ...restPayload } = payload;

      dispatch(saveMarketCampaignAudienceListProgress());
      dispatch(saveLoaderProgress());

      let finalSourceLink = null;

      if (localFile && localFile instanceof File) {
        try {
          const formData = new FormData();
          formData.append("files", localFile);
          formData.append("module_name", "WHATSAPP_MARKET_CAMPAIGN");
          formData.append("as_payload", JSON.stringify({}));

          const uploadResponse = await uploadFile({
            file: localFile,
            module_name: "WHATSAPP_MARKET_CAMPAIGN",
            defaultReturn: localFile.name || "",
            asPayload: {},
            onWarning: (message) => {
              dispatch(
                showMessage({
                  type: "warning",
                  message: message,
                  displayAs: "snackbar",
                })
              );
            },
          });

          if (uploadResponse) {
            finalSourceLink = uploadResponse;
          } else {
            throw new Error("No source_link returned from upload API");
          }
        } catch (uploadError: any) {
          dispatch(
            showMessage({
              type: "error",
              message:
                "Failed to upload file: " +
                (uploadError.response?.data?.message || uploadError.message),
              displayAs: "snackbar",
            })
          );
          throw uploadError;
        }
      }

      const sourceType = finalSourceLink ? getFileType(finalSourceLink) : "CSV";

      const requestPayload = {
        ...restPayload,
        source_link: [finalSourceLink],
        audience_source: sourceType,
      };

      if (finalSourceLink) {
        const res = await api.post(
          "/campaign/save-market-campaign-audience-list",
          requestPayload
        );
        onSuccess(true, res.data.data);
        dispatch(saveMarketCampaignAudienceListSuccess(res.data.data));
        dispatch(
          showMessage({
            type: "success",
            message: "Audience list saved successfully",
            displayAs: "snackbar",
          })
        );
        return res.data.data;
      }
    } catch (err: any) {
      onSuccess(false);
      dispatch(saveMarketCampaignAudienceListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to save audience list",
          displayAs: "snackbar",
        })
      );
      throw err;
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_MARKET_CAMPAIGN_DRAFT_STEPS_PROGRESS =
  "FETCH_MARKET_CAMPAIGN_DRAFT_STEPS_PROGRESS";
export const FETCH_MARKET_CAMPAIGN_DRAFT_STEPS_SUCCESS =
  "FETCH_MARKET_CAMPAIGN_DRAFT_STEPS_SUCCESS";
export const FETCH_MARKET_CAMPAIGN_DRAFT_STEPS_FAILED =
  "FETCH_MARKET_CAMPAIGN_DRAFT_STEPS_FAILED";

export const fetchMarketCampaignDraftStepsProgress = () =>
  action(FETCH_MARKET_CAMPAIGN_DRAFT_STEPS_PROGRESS);
export const fetchMarketCampaignDraftStepsSuccess = (
  data: IMarketCampaignDraftStep
) => action(FETCH_MARKET_CAMPAIGN_DRAFT_STEPS_SUCCESS, { data });
export const fetchMarketCampaignDraftStepsFailed = () =>
  action(FETCH_MARKET_CAMPAIGN_DRAFT_STEPS_FAILED);

export const fetchMarketCampaignDraftStepsAsync =
  (
    draft_step_uuid: string | null,
    onCallBack: (isSuccess?: boolean, data?: IMarketCampaignDraftStep) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchMarketCampaignDraftStepsProgress());

      const res = await api.get(
        `/campaign/get-draft-step?draft_step_uuid=${draft_step_uuid}`
      );
      dispatch(fetchMarketCampaignDraftStepsSuccess(res.data.data));
      onCallBack(true, res.data.data);
    } catch (err: any) {
      dispatch(fetchMarketCampaignDraftStepsFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to fetch draft step",
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertDraftStepAsync =
  (
    data: IMarketCampaignDraftStep,
    onSuccess?: (data: IMarketCampaignDraftStep) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      // Get current draft step data from state if it exists
      const currentState = getState();
      const existingDraftStep =
        currentState.marketCampaign.marketCampaignDraftSteps.data;

      // Merge new data with existing data
      const mergedData = {
        ...(existingDraftStep?.draft_step_data || {}), // Existing data
        ...data.draft_step_data, // New data
      };

      const payload = {
        ...data,
        draft_step_data: mergedData,
      };

      const res = await api.post("/campaign/upsert-draft-step", payload);
      dispatch(saveLoaderCompleted());

      dispatch(
        showMessage({
          type: "success",
          message: "Campaign draft step saved successfully",
          displayAs: "snackbar",
        })
      );

      onSuccess?.(res.data.data);
      return res.data.data;
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to save campaign draft step",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Audience List Columns Actions
export const FETCH_AUDIENCE_LIST_COLUMNS_PROGRESS =
  "FETCH_AUDIENCE_LIST_COLUMNS_PROGRESS";
export const FETCH_AUDIENCE_LIST_COLUMNS_SUCCESS =
  "FETCH_AUDIENCE_LIST_COLUMNS_SUCCESS";
export const FETCH_AUDIENCE_LIST_COLUMNS_FAILED =
  "FETCH_AUDIENCE_LIST_COLUMNS_FAILED";

export const fetchAudienceListColumnsProgress = () =>
  action(FETCH_AUDIENCE_LIST_COLUMNS_PROGRESS);
export const fetchAudienceListColumnsSuccess = (data: IAudienceListColumn) =>
  action(FETCH_AUDIENCE_LIST_COLUMNS_SUCCESS, { data });
export const fetchAudienceListColumnsFailed = () =>
  action(FETCH_AUDIENCE_LIST_COLUMNS_FAILED);

export const UPSERT_AUDIENCE_LIST_COLUMN_PROGRESS =
  "UPSERT_AUDIENCE_LIST_COLUMN_PROGRESS";
export const UPSERT_AUDIENCE_LIST_COLUMN_SUCCESS =
  "UPSERT_AUDIENCE_LIST_COLUMN_SUCCESS";
export const UPSERT_AUDIENCE_LIST_COLUMN_FAILED =
  "UPSERT_AUDIENCE_LIST_COLUMN_FAILED";

export const upsertAudienceListColumnProgress = () =>
  action(UPSERT_AUDIENCE_LIST_COLUMN_PROGRESS);
export const upsertAudienceListColumnSuccess = (data: IAudienceListColumn) =>
  action(UPSERT_AUDIENCE_LIST_COLUMN_SUCCESS, { data });
export const upsertAudienceListColumnFailed = () =>
  action(UPSERT_AUDIENCE_LIST_COLUMN_FAILED);

// Async Actions
export const fetchAudienceListColumnsAsync =
  (
    company_outlet_uuid: string,
    onCallBack?: (isSuccess?: boolean, data?: IAudienceListColumn) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchAudienceListColumnsProgress());

      let url = `/dataManagement/get-audience-list-column`;
      if (company_outlet_uuid) {
        url += `?company_outlet_uuid=${company_outlet_uuid}`;
      }
      const res = await api.get(url);

      dispatch(fetchAudienceListColumnsSuccess(res.data.data));
      onCallBack?.(true, res.data.data);
    } catch (err: any) {
      dispatch(fetchAudienceListColumnsFailed());
      onCallBack?.(false);
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message ||
            "Failed to fetch audience list columns",
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertAudienceListColumnAsync =
  (
    newColumn: string,
    company_outlet_uuid: string,
    onSuccess?: (data: IAudienceListColumn) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(upsertAudienceListColumnProgress());

      const currentState = getState();
      const existingData = currentState.marketCampaign.audienceListColumns.data;

      if (!existingData) {
        throw new Error("No existing audience list column data found");
      }

      const payload = {
        audience_list_column_uuid: existingData.audience_list_column_uuid
          ? existingData.audience_list_column_uuid
          : null,
        description: existingData.description ? existingData.description : null,
        additional_columns: [
          ...(existingData.additional_columns || []),
          newColumn,
        ],
        company_outlet_uuid: existingData.company_outlet_uuid
          ? existingData.company_outlet_uuid
          : company_outlet_uuid,
        status: existingData.status ? existingData.status : "ACTIVE",
      };

      const res = await api.post(
        "/dataManagement/upsert-audience-list-column",
        payload
      );

      dispatch(upsertAudienceListColumnSuccess(res.data.data));

      dispatch(
        showMessage({
          type: "success",
          message: "Audience list column added successfully",
          displayAs: "snackbar",
        })
      );

      onSuccess?.(res.data.data);
    } catch (err: any) {
      dispatch(upsertAudienceListColumnFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to add audience list column",
          displayAs: "snackbar",
        })
      );
    }
  };

// ############################################### Refactored Code ###############################################

// Single Market Campaign Actions
export const FETCH_SINGLE_MARKET_CAMPAIGN_PROGRESS =
  "FETCH_SINGLE_MARKET_CAMPAIGN_PROGRESS";
export const FETCH_SINGLE_MARKET_CAMPAIGN_SUCCESS =
  "FETCH_SINGLE_MARKET_CAMPAIGN_SUCCESS";
export const FETCH_SINGLE_MARKET_CAMPAIGN_FAILED =
  "FETCH_SINGLE_MARKET_CAMPAIGN_FAILED";

export const fetchSingleMarketCampaignProgress = () =>
  action(FETCH_SINGLE_MARKET_CAMPAIGN_PROGRESS);
export const fetchSingleMarketCampaignSuccess = (
  data: IMarketCampaignBasicInfo,
  draftStep: IMarketCampaignDraftStep
) => action(FETCH_SINGLE_MARKET_CAMPAIGN_SUCCESS, { data, draftStep });
export const fetchSingleMarketCampaignFailed = (errorMessage: string) =>
  action(FETCH_SINGLE_MARKET_CAMPAIGN_FAILED, { errorMessage });

export const fetchSingleMarketCampaignAsync =
  (
    uuid: string,
    company_outlet_uuid: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchSingleMarketCampaignProgress());
      // Fetch both in parallel
      const [campaignRes, draftRes] = await Promise.allSettled([
        api.get(
          `/campaign/get-campaigns?market_campaign_uuid=${uuid}&company_outlet_uuid=${company_outlet_uuid}`
        ),
        api.get(`/campaign/get-draft-step?draft_step_uuid=${uuid}`),
      ]);

      // Validate both responses
      if (
        campaignRes.status === "fulfilled" &&
        draftRes.status === "fulfilled"
      ) {
        const campaignList = campaignRes.value?.data?.data ?? [];
        const draftData = draftRes.value?.data?.data ?? [];

        if (campaignList.length > 0) {
          dispatch(
            fetchSingleMarketCampaignSuccess(campaignList[0], draftData)
          );
        } else {
          dispatch(fetchSingleMarketCampaignFailed("Campaign not found"));
        }
      } else {
        // Handle rejected promises
        const errorMessage =
          (campaignRes.status === "rejected" &&
            campaignRes.reason?.response?.data?.message) ||
          (draftRes.status === "rejected" &&
            draftRes.reason?.response?.data?.message) ||
          "Failed to fetch campaign";

        dispatch(fetchSingleMarketCampaignFailed(errorMessage));
        dispatch(
          showMessage({
            type: "error",
            message: errorMessage,
            displayAs: "snackbar",
          })
        );
      }
    } catch (err: any) {
      dispatch(
        fetchSingleMarketCampaignFailed(
          err.response?.data?.message || "Failed to fetch campaign"
        )
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to fetch campaign",
          displayAs: "snackbar",
        })
      );
    }
  };

interface IUpsertSingleMarketCampaign {
  market_campaign: IMarketCampaignBasicInfo;
  draft_state: IMarketCampaignAudience;
}

export const upsertSingleMarketCampaignAsync =
  (
    payload: IUpsertSingleMarketCampaign,
    onSuccess?: (data: IUpsertSingleMarketCampaign) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      // Fetch both in parallel
      const [campaignRes, draftRes] = await Promise.all([
        api.post("/campaign/upsert-campaign", payload.market_campaign),
        api.post("/campaign/upsert-draft-step", payload.draft_state),
      ]);

      const campaignData = campaignRes.data.data;
      const draftData = draftRes.data.data;

      onSuccess?.({ market_campaign: campaignData, draft_state: draftData });
      dispatch(
        showMessage({
          type: "success",
          message: "Campaign basic info saved successfully",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to save campaign basic info",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// ############################################### CLEAR STATE ###############################################

export const CLEAR_MARKET_CAMPAIGN_STATE = "CLEAR_MARKET_CAMPAIGN_STATE";
export const CLEAR_DRAFT_STEP_STATE = "CLEAR_DRAFT_STEP_STATE";
export const clearDraftStepState = () => action(CLEAR_DRAFT_STEP_STATE);
export const clearMerketCampaignState = () =>
  action(CLEAR_MARKET_CAMPAIGN_STATE);
